import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import ListItemIcon from "@mui/material/ListItemIcon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import Settings from "@mui/icons-material/Settings";
import Logout from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InterestsIcon from "@mui/icons-material/Interests";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import { ThemeContext } from "context/ThemeContext";
import { AuthContext } from "context/Authentication";
import { redirectToLoginWithReturn } from "utils/authUtils";
import Wishlist from "CommonComponents/Wishlist/Wishlist.jsx";
import { postRecord } from "services/services";
import { Home } from "@mui/icons-material";

const API_GetCartByUserId = "cart/getbyuserid";
const API_GetWishlistByUserId = "wishlist/getbyuserid";

export default function HeaderActions({ headerRefresh, setHeaderRefresh }) {
  const themeMode = useContext(ThemeContext);
  const darkMode = themeMode.state.darkMode;
  const { dispatch } = useContext(AuthContext);
  const context = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [isIconSwitching, setIsIconSwitching] = useState(false);
  const open = Boolean(anchorEl);

  // Determine if audiobook mode is active
  const isAudioBook =
    context.state.companyInfo?.Product_Service_Type === "Product - Digital";

  useEffect(() => {
    if (context.state.isAuthenticated && headerRefresh) {
      fetchCartCount();
      fetchWishlistCount();
      setHeaderRefresh(false);
    }
  }, [context.state.isAuthenticated, headerRefresh]);

  const fetchCartCount = async () => {
    try {
      const response = await postRecord(API_GetCartByUserId, {
        User_Id: context.state.user.User_Id,
      });
      if (response.status === 200 && response.data) {
        setCartCount(response.data.length);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchWishlistCount = async () => {
    try {
      const response = await postRecord(API_GetWishlistByUserId, {
        User_Id: context.state.user.User_Id,
      });
      if (response.status === 200 && response.data) {
        setWishlistCount(response.data.length);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    dispatch({ type: "LOGOUT" });
  };

  const handleWishlistOpen = () => {
    setWishlistOpen(true);
  };

  const handleWishlistClose = () => {
    setWishlistOpen(false);
  };
  // Hide cart if current URL contains "SchoolZones"
  const hideCart = window.location.href.includes("SchoolZones");

  return (
    <Box
      sx={{
        flexGrow: 0,
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.5, sm: 1 },
        justifyContent: "flex-end",
      }}
    >
      {!isAudioBook && (
        <>
          {!hideCart && (
            <>
              <Tooltip title="My Cart">
                <IconButton onClick={() => navigate("/AddToCart")}>
                  <Badge badgeContent={cartCount} color="primary">
                    <ShoppingCartOutlinedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title="My Wishlist">
                <IconButton onClick={handleWishlistOpen}>
                  <Badge badgeContent={wishlistCount} color="primary">
                    <InterestsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            </>
          )}
        </>
      )}
      {/* <Tooltip title="Notifications">
        <IconButton>
          <NotificationsNoneOutlinedIcon />
        </IconButton>
      </Tooltip> */}
      {context.state.isAuthenticated ? (
        <Tooltip title="My Account">
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{ ml: 1 }}
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor:
                  !context.state.user.Profile_Image ||
                  context.state.user.Profile_Image.trim() === ""
                    ? "primary.main"
                    : undefined,
              }}
              src={
                context.state.user.Profile_Image &&
                context.state.user.Profile_Image.trim() !== ""
                  ? process.env.BASEURL + context.state.user.Profile_Image
                  : undefined
              }
            >
              {(!context.state.user.Profile_Image ||
                context.state.user.Profile_Image.trim() === "") &&
                context.state.user.Full_Name &&
                context.state.user.Full_Name.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)}
            </Avatar>
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            const currentPath = location.pathname + location.search;
            // console.log("Current Path:", currentPath);
            redirectToLoginWithReturn(navigate, currentPath);
          }}
          sx={{
            ml: 1,
            position: "relative",
            overflow: "hidden",
            borderRadius: "25px",
            px: { xs: 1.5, sm: 3 },
            py: { xs: 0.5, sm: 1 },
            fontWeight: 600,
            textTransform: "none",
            fontSize: { xs: "0.75rem", sm: "0.95rem" },
            borderWidth: "2px",
            background: "linear-gradient(45deg, transparent, transparent)",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 0.5, sm: 1 },
            minHeight: { xs: "32px", sm: "40px" },
            minWidth: { xs: "auto", sm: "auto" },
            whiteSpace: "nowrap",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "-100%",
              width: "100%",
              height: "100%",
              background: (theme) =>
                `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 100%)`,
              transition: "left 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
              zIndex: -1,
            },
            "&:hover": {
              color: "white",
              borderColor: "transparent",
              transform: "translateY(-2px)",
              boxShadow: (theme) =>
                `0 12px 30px ${theme.palette.primary.main}40, 0 6px 20px ${theme.palette.secondary.main}30`,
              "&::before": {
                left: 0,
              },
              "& .button-icon": {
                transform: "translateX(8px) scale(1.1)",
                opacity: 0,
              },
              "& .button-text": {
                transform: "translateX(-8px)",
              },
              "& .hover-icon": {
                transform: "translateX(0px) scale(1.1)",
                opacity: 1,
              },
            },
            "&:active": {
              transform: "translateY(0px)",
            },
            "& .button-icon": {
              fontSize: { xs: "0.9rem", sm: "1.25rem" },
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: "translateX(0px) scale(1)",
              opacity: 1,
            },
            "& .button-text": {
              transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: "translateX(0px)",
              display: "inline", // Always show text, just make it smaller on mobile
            },
            "& .hover-icon": {
              position: "absolute",
              left: { xs: "6px", sm: "16px" },
              fontSize: { xs: "0.9rem", sm: "1.25rem" },
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: "translateX(-8px) scale(1)",
              opacity: 0,
            },
          }}
        >
          <AccountCircleOutlinedIcon className="button-icon" />
          <ArrowForwardIcon className="hover-icon" />
          <span className="button-text">Sign In/Sign Up</span>
        </Button>
      )}{" "}
      <Tooltip title="Toggle Color Mode">
        <IconButton
          aria-label="darkmode"
          sx={{
            ml: 1,
            width: 36,
            height: 36,
            background: "transparent",
            border: "none",
            transition: "transform 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "transparent",
              transform: "scale(1.05)",
              "& .MuiSvgIcon-root": {
                transform: "rotate(360deg) scale(1)",
                filter: darkMode
                  ? "drop-shadow(0 0 20px rgba(255, 215, 0, 1)) drop-shadow(0 0 40px rgba(255, 215, 0, 0.8))"
                  : "drop-shadow(0 0 20px rgba(74, 144, 226, 1)) drop-shadow(0 0 40px rgba(74, 144, 226, 0.8))",
              },
            },
            "&:active": {
              backgroundColor: "transparent",
              transform: "scale(0.95)",
            },
            "& .MuiSvgIcon-root": {
              fontSize: "1.3rem",
              transition: "all 1.2s cubic-bezier(0.4, 0, 0.2, 1)", // Slower transition for smooth rotation
              color: darkMode ? "#FFD700" : "#4A90E2",
              filter: darkMode
                ? "drop-shadow(0 0 12px rgba(255, 215, 0, 0.8)) drop-shadow(0 0 24px rgba(255, 215, 0, 0.4))"
                : "drop-shadow(0 0 12px rgba(74, 144, 226, 0.8)) drop-shadow(0 0 24px rgba(74, 144, 226, 0.4))",
              transformOrigin: "center",
              transform: isIconSwitching
                ? "translateX(0px) scale(1)"
                : "rotate(0deg) scale(1)",
              animation: isIconSwitching
                ? "slideFromLeft 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards"
                : "none",
            },
            "&:active .MuiSvgIcon-root": {
              transform: "rotate(0deg) scale(0.9)",
              transition: "all 0.1s ease-out",
            },
            "@keyframes slideFromLeft": {
              "0%": {
                transform: "translateX(-40px) scale(0.5)",
                opacity: 0,
              },
              "50%": {
                transform: "translateX(5px) scale(1.1)",
                opacity: 0.8,
              },
              "100%": {
                transform: "translateX(0px) scale(1)",
                opacity: 1,
              },
            },
          }}
          onClick={() => {
            // Trigger slide animation
            setIsIconSwitching(true);

            // Trigger theme change immediately
            darkMode
              ? themeMode.dispatch({ type: "LIGHTMODE" })
              : themeMode.dispatch({ type: "DARKMODE" });

            // Reset animation state after animation completes
            setTimeout(() => {
              setIsIconSwitching(false);
            }, 800);
          }}
        >
          {darkMode ? <LightModeIcon /> : <NightsStayIcon />}
        </IconButton>
      </Tooltip>
      {context.state.isAuthenticated && (
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem>
            <Avatar
              src={
                context.state.user.Profile_Image &&
                context.state.user.Profile_Image.trim() !== ""
                  ? process.env.BASEURL + context.state.user.Profile_Image
                  : undefined
              }
              sx={{
                bgcolor:
                  !context.state.user.Profile_Image ||
                  context.state.user.Profile_Image.trim() === ""
                    ? "primary.main"
                    : undefined,
              }}
            >
              {(!context.state.user.Profile_Image ||
                context.state.user.Profile_Image.trim() === "") &&
                context.state.user.Full_Name &&
                context.state.user.Full_Name.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight="bold">
                {context.state.user.Full_Name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {context.state.user.User_Email}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => navigate("/Account")}>
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            Account
          </MenuItem>
          {/* Audiobook mode: Only show My Subscriptions and Account */}
          {isAudioBook ? (
            <MenuItem onClick={() => navigate("/MySubscriptions")}>
              <ListItemIcon>
                <ListAltIcon fontSize="small" />
              </ListItemIcon>
              My Subscriptions
            </MenuItem>
          ) : (
            <>
              <MenuItem onClick={() => navigate("/MyOrders")}>
                <ListItemIcon>
                  <ListAltIcon fontSize="small" />
                </ListItemIcon>
                My Orders
              </MenuItem>
              <MenuItem onClick={handleWishlistOpen}>
                <ListItemIcon>
                  <FavoriteIcon fontSize="small" />
                </ListItemIcon>
                Wishlist
              </MenuItem>
              {!hideCart && (
                <MenuItem onClick={() => navigate("/AddToCart")}>
                  <ListItemIcon>
                    <LocalMallIcon fontSize="small" />
                  </ListItemIcon>
                  Cart
                </MenuItem>
              )}
              <MenuItem onClick={() => navigate("/Coupons")}>
                <ListItemIcon>
                  <LocalOfferIcon fontSize="small" />
                </ListItemIcon>
                Coupons
              </MenuItem>
              <MenuItem onClick={() => navigate("/Offers")}>
                <ListItemIcon>
                  <LocalOfferIcon fontSize="small" />
                </ListItemIcon>
                Offers
              </MenuItem>
              <MenuItem onClick={() => navigate("/MyAddresses")}>
                <ListItemIcon>
                  <LocationOnIcon fontSize="small" />
                </ListItemIcon>
                Saved Addresses
              </MenuItem>
              {/* Show School Zone related items only if enabled in app settings */}
              {/* {context.state?.appSettings?.some(
                (setting) =>
                  setting.Setting_Name === "School Zone" &&
                  setting.Setting_Value === "Enabled"
              ) && (
                <>
                  <MenuItem onClick={() => navigate("/Beneficiaries")}>
                    <ListItemIcon>
                      <PeopleIcon fontSize="small" />
                    </ListItemIcon>
                    Student List
                  </MenuItem>
                  <MenuItem onClick={() => navigate("/SchoolZones")}>
                    <ListItemIcon>
                      <SchoolIcon fontSize="small" />
                    </ListItemIcon>
                    School Zones
                  </MenuItem>
                  <MenuItem onClick={() => navigate("/SchoolMenuLanding")}>
                    <ListItemIcon>
                      <SchoolIcon fontSize="small" />
                    </ListItemIcon>
                    School Menu
                  </MenuItem>
                </>
              )} */}
            </>
          )}
          <Divider />
          <MenuItem onClick={() => navigate(context.state?.user?.Default_Page)}>
            <ListItemIcon>
              <Home fontSize="small" />
            </ListItemIcon>
            Home
          </MenuItem>
          <MenuItem>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            <Button
              variant="text"
              fullWidth
              size="large"
              onClick={handleSignOut}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
              }}
            >
              Log Out
            </Button>
          </MenuItem>
        </Menu>
      )}
      <Dialog
        open={wishlistOpen}
        onClose={handleWishlistClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>My Wishlist</DialogTitle>
        <DialogContent>
          <Wishlist setHeaderRefresh={setHeaderRefresh} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
